// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

export interface Definition {
  term: string;
  body: string;
}

export const GLOSSARY: Record<string, Definition> = {
  embedding: {
    term: 'Embedding',
    body: 'A list of numbers standing in for a piece of text, arranged so that texts about the same thing end up pointing in the same direction. Comparing two of them is how "stop retrying forever" finds a page about backoff.',
  },
  chunk: {
    term: 'Chunk',
    body: 'A search result is a passage, not a whole page. Searchpack splits each document at its headings and then at sentence boundaries, so a hit lands you on the paragraph that answers your question.',
  },
  mrl: {
    term: 'Matryoshka truncation (MRL)',
    body: 'This model was trained so the first N numbers of each vector are useful on their own. Keeping 256 of 1,024 costs a little accuracy and quarters the size of everything you ship.',
  },
  wordpiece: {
    term: 'WordPiece',
    body: 'The way text is cut into vocabulary units before it is embedded. Common words stay whole; rarer ones split, so "deduplication" might become "de", "##dup", "##lication".',
  },
  quantisation: {
    term: 'Quantisation',
    body: 'Storing each number as one byte instead of four. It shrinks the pack fourfold for a small, measurable loss of precision — about 0.001 of cosine similarity here, 0.013 at worst.',
  },
  hamming: {
    term: 'Binary code / Hamming distance',
    body: 'Each vector is also stored as 256 bits — one per dimension, recording only whether it was positive. Comparing bits is far faster than comparing numbers, so a first pass narrows thousands of chunks to a shortlist that then gets scored properly.',
  },
  cosine: {
    term: 'Cosine similarity',
    body: 'The angle between two embeddings, scored from -1 to 1. It ignores how long the texts were and asks only whether they point the same way.',
  },
  bm25: {
    term: 'BM25',
    body: 'The classic keyword ranking used by search engines for decades. It rewards rare words that appear often in one document — exactly the flag names and error strings that averaged embeddings handle worst.',
  },
};

/**
 * Click-to-define tooltips. Pointer-anchored, dismissed by Escape, an outside
 * click, or scrolling away.
 */
export function initGlossary(tooltip: HTMLElement): void {
  const hide = (): void => {
    tooltip.hidden = true;
  };

  document.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement | null)?.closest?.('.glossary-link') as HTMLElement | null;
    if (!target) {
      if (!tooltip.contains(event.target as Node)) hide();
      return;
    }
    const key = target.dataset.term ?? '';
    const def = GLOSSARY[key];
    if (!def) {
      hide();
      return;
    }
    event.preventDefault();
    tooltip.replaceChildren();
    const strong = document.createElement('strong');
    strong.textContent = def.term;
    tooltip.append(strong, document.createTextNode(def.body));
    tooltip.hidden = false;

    const rect = target.getBoundingClientRect();
    const width = Math.min(tooltip.offsetWidth || 320, window.innerWidth - 16);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const below = rect.bottom + 8;
    const height = tooltip.offsetHeight || 120;
    tooltip.style.left = `${left}px`;
    tooltip.style.top = below + height > window.innerHeight ? `${Math.max(8, rect.top - height - 8)}px` : `${below}px`;
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hide();
  });
  window.addEventListener('scroll', hide, { passive: true });
  window.addEventListener('resize', hide);
}
